import { useEffect, useState } from "react";
import { Users, Package, Clock, Crown, Flag, Check, X, Ban, ShieldCheck, TrendingUp, Euro, Ticket, Copy } from "lucide-react";
import { toast } from "sonner";
import { api, mediaUrl, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

export default function Admin() {
  const [stats, setStats] = useState(null);
  const [pending, setPending] = useState([]);
  const [users, setUsers] = useState([]);
  const [reports, setReports] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [codes, setCodes] = useState([]);
  const [codeForm, setCodeForm] = useState({ credits: 10, quantity: 5 });

  const load = async () => {
    const [s, p, u, r, a, c] = await Promise.all([
      api.get("/admin/stats"),
      api.get("/admin/listings", { params: { status: "pending" } }),
      api.get("/admin/users"),
      api.get("/admin/reports"),
      api.get("/admin/analytics"),
      api.get("/admin/credit-codes"),
    ]);
    setStats(s.data); setPending(p.data); setUsers(u.data); setReports(r.data); setAnalytics(a.data); setCodes(c.data);
  };

  const genCodes = async () => {
    try {
      const { data } = await api.post("/admin/credit-codes", { credits: Number(codeForm.credits), quantity: Number(codeForm.quantity) });
      toast.success(`${data.length} codici generati`);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  useEffect(() => { load().catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail))); }, []);

  const moderate = async (id, action) => {
    try { await api.post(`/admin/listings/${id}/${action}`); toast.success(action === "approve" ? "Approvato" : "Rifiutato"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const banToggle = async (id, banned) => {
    try { await api.post(`/admin/users/${id}/${banned ? "unban" : "ban"}`); toast.success(banned ? "Sbloccato" : "Bloccato"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const resolveReport = async (id) => {
    try { await api.post(`/admin/reports/${id}/resolve`); toast.success("Segnalazione risolta"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  if (!stats) return null;

  const statCards = [
    { label: "Utenti", value: stats.users, icon: Users },
    { label: "Annunci", value: stats.listings, icon: Package },
    { label: "In moderazione", value: stats.pending, icon: Clock, alert: stats.pending > 0 },
    { label: "Premium", value: stats.premium_users, icon: Crown },
    { label: "Consegnati", value: stats.delivered, icon: Check },
    { label: "Segnalazioni", value: stats.reports, icon: Flag, alert: stats.reports > 0 },
  ];

  return (
    <div className="max-w-5xl mx-auto">
      <h1 className="font-head font-extrabold text-3xl sm:text-4xl tracking-tight mb-6">Dashboard Admin</h1>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-8">
        {statCards.map((s) => (
          <div key={s.label} className={`rounded-2xl border p-4 ${s.alert ? "border-accent/50 bg-accent/5" : "border-border/60 bg-card"}`} data-testid={`admin-stat-${s.label}`}>
            <s.icon className={`h-5 w-5 ${s.alert ? "text-accent" : "text-primary"}`} />
            <div className="font-head font-extrabold text-2xl mt-2">{s.value}</div>
            <div className="text-[11px] text-muted-foreground uppercase tracking-wider">{s.label}</div>
          </div>
        ))}
      </div>

      <Tabs defaultValue="moderation">
        <TabsList className="rounded-full">
          <TabsTrigger value="moderation" className="rounded-full" data-testid="admin-tab-moderation">Moderazione ({pending.length})</TabsTrigger>
          <TabsTrigger value="users" className="rounded-full" data-testid="admin-tab-users">Utenti</TabsTrigger>
          <TabsTrigger value="reports" className="rounded-full" data-testid="admin-tab-reports">Segnalazioni ({reports.filter(r=>r.status==="open").length})</TabsTrigger>
          <TabsTrigger value="analytics" className="rounded-full" data-testid="admin-tab-analytics">Analytics</TabsTrigger>
          <TabsTrigger value="codes" className="rounded-full" data-testid="admin-tab-codes">Codici crediti</TabsTrigger>
        </TabsList>

        <TabsContent value="moderation" className="mt-6 space-y-3">
          {pending.length === 0 ? <p className="text-muted-foreground text-center py-10">Nessun annuncio da moderare.</p> : pending.map((l) => (
            <div key={l.id} className="rounded-2xl border border-border/60 p-4 flex items-center gap-4" data-testid={`mod-listing-${l.id}`}>
              <div className="h-16 w-16 rounded-xl bg-secondary overflow-hidden shrink-0">{l.images?.[0] && <img src={mediaUrl(l.images[0])} alt="" className="h-full w-full object-cover" />}</div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold truncate">{l.title}</div>
                <div className="text-xs text-muted-foreground">{l.category} · {l.city} · di {l.owner_name}</div>
                <p className="text-xs text-muted-foreground mt-1 line-clamp-1">{l.description}</p>
              </div>
              <Button size="icon" className="rounded-full h-9 w-9 bg-success hover:bg-success/90" onClick={() => moderate(l.id, "approve")} data-testid={`approve-${l.id}`}><Check className="h-4 w-4" /></Button>
              <Button size="icon" variant="outline" className="rounded-full h-9 w-9" onClick={() => moderate(l.id, "reject")} data-testid={`reject-${l.id}`}><X className="h-4 w-4" /></Button>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="users" className="mt-6 space-y-2">
          {users.map((u) => (
            <div key={u.user_id} className="rounded-2xl border border-border/60 p-3 flex items-center gap-3" data-testid={`admin-user-${u.user_id}`}>
              <div className="flex-1 min-w-0">
                <div className="font-semibold truncate flex items-center gap-2">{u.name}
                  {u.role === "admin" && <Badge variant="secondary" className="text-[10px]">Admin</Badge>}
                  {u.plan === "premium" && <Crown className="h-3.5 w-3.5 text-[hsl(var(--gold))]" />}
                  {u.identity_verified && <ShieldCheck className="h-3.5 w-3.5 text-success" />}
                </div>
                <div className="text-xs text-muted-foreground">{u.email} · {u.credits} crediti · {u.donations_count} donazioni</div>
              </div>
              {u.role !== "admin" && (
                <Button size="sm" variant={u.banned ? "outline" : "ghost"} className={`rounded-full ${u.banned ? "" : "text-destructive"}`} onClick={() => banToggle(u.user_id, u.banned)} data-testid={`ban-${u.user_id}`}>
                  <Ban className="h-4 w-4 mr-1" /> {u.banned ? "Sblocca" : "Blocca"}
                </Button>
              )}
            </div>
          ))}
        </TabsContent>

        <TabsContent value="reports" className="mt-6 space-y-3">
          {reports.length === 0 ? <p className="text-muted-foreground text-center py-10">Nessuna segnalazione.</p> : reports.map((r) => (
            <div key={r.id} className="rounded-2xl border border-border/60 p-4 flex items-center gap-4" data-testid={`report-${r.id}`}>
              <Flag className={`h-5 w-5 ${r.status === "open" ? "text-accent" : "text-muted-foreground"}`} />
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm">{r.target_type === "user" ? "Utente" : "Annuncio"} segnalato</div>
                <div className="text-xs text-muted-foreground">{r.reason} — da {r.reporter_name}</div>
              </div>
              {r.status === "open" ? <Button size="sm" variant="outline" className="rounded-full" onClick={() => resolveReport(r.id)} data-testid={`resolve-${r.id}`}>Risolvi</Button> : <Badge variant="secondary">Risolta</Badge>}
            </div>
          ))}
        </TabsContent>

        <TabsContent value="analytics" className="mt-6">
          {analytics && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="rounded-2xl border border-border/60 bg-card p-4" data-testid="an-revenue">
                  <Euro className="h-5 w-5 text-success" /><div className="font-head font-extrabold text-2xl mt-2">€{analytics.revenue_eur}</div><div className="text-[11px] text-muted-foreground uppercase tracking-wider">Ricavi</div>
                </div>
                <div className="rounded-2xl border border-border/60 bg-card p-4" data-testid="an-paid">
                  <TrendingUp className="h-5 w-5 text-primary" /><div className="font-head font-extrabold text-2xl mt-2">{analytics.paid_transactions}</div><div className="text-[11px] text-muted-foreground uppercase tracking-wider">Pagamenti</div>
                </div>
                <div className="rounded-2xl border border-border/60 bg-card p-4" data-testid="an-referred">
                  <Users className="h-5 w-5 text-accent" /><div className="font-head font-extrabold text-2xl mt-2">{analytics.referred_users}</div><div className="text-[11px] text-muted-foreground uppercase tracking-wider">Da inviti</div>
                </div>
                <div className="rounded-2xl border border-border/60 bg-card p-4" data-testid="an-sources">
                  <Flag className="h-5 w-5 text-primary" /><div className="font-head font-extrabold text-2xl mt-2">{analytics.acquisition_by_source.length}</div><div className="text-[11px] text-muted-foreground uppercase tracking-wider">Canali</div>
                </div>
              </div>

              <div>
                <h3 className="font-head font-bold text-lg mb-3">Acquisizione per canale (UTM)</h3>
                <div className="rounded-2xl border border-border/60 divide-y divide-border/60 overflow-hidden">
                  {analytics.acquisition_by_source.map((s) => (
                    <div key={s.source} className="flex items-center justify-between px-4 py-3 text-sm" data-testid={`src-${s.source}`}>
                      <span className="font-medium">{s.source}</span><Badge variant="secondary">{s.count} utenti</Badge>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="font-head font-bold text-lg mb-3">Top inviti (referral)</h3>
                {analytics.top_referrers.length === 0 ? <p className="text-muted-foreground text-sm">Ancora nessun invito a segno.</p> : (
                  <div className="rounded-2xl border border-border/60 divide-y divide-border/60 overflow-hidden">
                    {analytics.top_referrers.map((r) => (
                      <div key={r.referral_code} className="flex items-center justify-between px-4 py-3 text-sm">
                        <span className="font-medium">{r.name} <span className="text-muted-foreground">({r.referral_code})</span></span>
                        <Badge className="bg-primary text-primary-foreground">{r.referrals_count} inviti</Badge>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="codes" className="mt-6">
          <div className="rounded-2xl border border-border/60 bg-card p-6 tactile-shadow">
            <h3 className="font-head font-bold text-lg flex items-center gap-2"><Ticket className="h-5 w-5 text-primary" /> Genera codici crediti prepagati</h3>
            <p className="text-sm text-muted-foreground mt-1">Crea codici da distribuire (es. volantini, eventi, ricariche). Gli utenti li riscattano dalla pagina Premium.</p>
            <div className="flex flex-col sm:flex-row gap-3 mt-4">
              <div className="flex-1"><label className="text-xs text-muted-foreground">Crediti per codice</label><Input type="number" min="1" value={codeForm.credits} onChange={(e) => setCodeForm({ ...codeForm, credits: e.target.value })} className="rounded-xl h-11" data-testid="code-credits" /></div>
              <div className="flex-1"><label className="text-xs text-muted-foreground">Quantità</label><Input type="number" min="1" value={codeForm.quantity} onChange={(e) => setCodeForm({ ...codeForm, quantity: e.target.value })} className="rounded-xl h-11" data-testid="code-quantity" /></div>
              <Button onClick={genCodes} className="rounded-full h-11 px-6 self-end" data-testid="gen-codes-btn">Genera</Button>
            </div>
          </div>

          <div className="mt-6 rounded-2xl border border-border/60 divide-y divide-border/60 overflow-hidden">
            {codes.length === 0 ? <p className="text-muted-foreground text-center py-8 text-sm">Nessun codice generato.</p> : codes.map((c) => (
              <div key={c.code} className="flex items-center justify-between px-4 py-3 text-sm" data-testid={`code-${c.code}`}>
                <span className="font-mono font-semibold">{c.code}</span>
                <span className="text-muted-foreground">{c.credits} crediti</span>
                {c.redeemed_by ? <Badge variant="secondary">Usato</Badge> : (
                  <div className="flex items-center gap-2">
                    <Badge className="bg-success text-white border-0">Attivo</Badge>
                    <Button size="icon" variant="ghost" className="h-8 w-8 rounded-full" onClick={() => { navigator.clipboard.writeText(c.code); toast.success("Codice copiato"); }} data-testid={`copy-code-${c.code}`}><Copy className="h-4 w-4" /></Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
